import { useRef, useState } from 'react';
import {
  MAFIA_COUNTS,
  MAX_NAME_LENGTH,
  MAX_PLAYERS,
  MIN_PLAYERS,
  allowedMafiaCounts,
  clampMafiaCount,
  isMafiaCountValid,
  isValidPlayerName,
  normalizeNameKey,
  sanitizePlayerName,
  type MafiaCount,
  type StartIssue,
} from '@bm/shared';
import { de } from '@/i18n/de';
import { avatarColor } from '@/lib/avatar';
import type { LocalRoundSetup } from '@/lib/localRound';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/Button';

interface LocalSetupScreenProps {
  setup: LocalRoundSetup;
  onChange: (setup: LocalRoundSetup) => void;
  onDeal: () => void;
  onBack: () => void;
}

export function LocalSetupScreen({ setup, onChange, onDeal, onBack }: LocalSetupScreenProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { names, mafiaCount } = setup;
  const allowed = allowedMafiaCounts(names.length);
  const issue: StartIssue | null =
    names.length < MIN_PLAYERS
      ? 'NOT_ENOUGH_PLAYERS'
      : isMafiaCountValid(names.length, mafiaCount)
        ? null
        : 'INVALID_MAFIA_COUNT';

  const addName = () => {
    const clean = sanitizePlayerName(draft);
    if (!isValidPlayerName(clean)) return;

    if (names.length >= MAX_PLAYERS) {
      setError(de.local.full(MAX_PLAYERS));
      return;
    }
    if (names.some((name) => normalizeNameKey(name) === normalizeNameKey(clean))) {
      setError(de.local.duplicate);
      return;
    }

    const next = [...names, clean];
    onChange({ names: next, mafiaCount: clampMafiaCount(next.length, mafiaCount) });
    setDraft('');
    setError(null);
    // Fokus behalten: mehrere Namen hintereinander einzutippen ist der Normalfall.
    inputRef.current?.focus();
  };

  const removeName = (index: number) => {
    const next = names.filter((_, i) => i !== index);
    onChange({ names: next, mafiaCount: clampMafiaCount(next.length, mafiaCount) });
    setError(null);
  };

  return (
    <AppShell
      footer={
        <div className="mx-auto flex max-w-xl flex-col gap-2">
          {issue && (
            <p className="text-center text-sm text-fg-muted">
              {de.lobby.startIssue(issue, MIN_PLAYERS, names.length)}
            </p>
          )}
          <Button fullWidth disabled={issue !== null} onClick={onDeal}>
            {de.local.deal}
          </Button>
        </div>
      }
    >
      <div className="mx-auto flex max-w-xl flex-col gap-6 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="self-start text-sm text-fg-muted hover:text-fg"
        >
          {de.common.back}
        </button>

        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h1 className="font-display text-2xl font-bold">{de.local.setupTitle}</h1>
            <span className="shrink-0 text-sm text-fg-muted">
              {de.local.playerCount(names.length, MAX_PLAYERS)}
            </span>
          </div>

          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              addName();
            }}
          >
            <input
              ref={inputRef}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setError(null);
              }}
              maxLength={MAX_NAME_LENGTH}
              placeholder={de.local.namePlaceholder}
              enterKeyHint="done"
              autoComplete="off"
              className="min-h-14 min-w-0 flex-1 rounded-2xl border border-surface-border bg-surface px-4 text-lg text-fg outline-none placeholder:text-fg-faint focus:border-accent"
              style={{ fontSize: '16px' }}
            />
            <Button
              type="submit"
              variant="secondary"
              className="shrink-0 px-4 text-sm"
              disabled={!isValidPlayerName(draft)}
            >
              {de.local.add}
            </Button>
          </form>

          {error && <p className="mt-2 text-sm text-fg-muted">{error}</p>}

          <ul className="mt-3 flex flex-col gap-2">
            {names.map((name, index) => (
              <li
                key={name}
                className="flex items-center gap-3 rounded-xl border border-surface-border bg-surface px-3 py-2.5"
              >
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold text-black/80"
                  style={{ background: avatarColor(name) }}
                  aria-hidden="true"
                >
                  {name.charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
                <button
                  type="button"
                  onClick={() => removeName(index)}
                  aria-label={de.local.remove(name)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-fg-faint hover:bg-surface-2 hover:text-fg"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-2 font-display text-lg font-semibold">{de.local.mafiaCount}</h2>
          <div className="flex gap-2">
            {MAFIA_COUNTS.map((count) => {
              const active = mafiaCount === count;
              return (
                <button
                  key={count}
                  type="button"
                  disabled={!allowed.includes(count)}
                  onClick={() => onChange({ names, mafiaCount: count as MafiaCount })}
                  className={`min-h-11 flex-1 rounded-xl font-display font-semibold transition-colors disabled:opacity-30 ${
                    active ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-fg'
                  }`}
                >
                  {count}
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
